import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RegiAssistantPanel } from "@getregistered/greg-editors";
import { getConsumer, getSubscription } from "../actionCableConsumerPool";

function readPageContext() {
  try {
    return JSON.parse(document.body.dataset.regiContext || "null");
  } catch {
    return null;
  }
}

function readOrgId() {
  const raw = document.body.dataset.regiOrganizationId;
  return raw ? Number(raw) : null;
}

// Applied proposals mutate the current page's data; reload it in place.
function refreshAfterApply(data) {
  // The form editor syncs changes itself; a reload would remount it.
  if (document.querySelector('[data-controller~="collaborative-form-editor"]')) return;
  if (data.type === "proposal_status" && data.status === "applied" && !data.error && !data.client_applied && document.hasFocus()) {
    if (!window.Turbo) return;
    // .layout-content is the scroll container, not the window, so restore it by hand.
    const scrollTop = document.querySelector(".layout-content")?.scrollTop ?? 0;
    const href = window.location.href;
    document.addEventListener(
      "turbo:load",
      () => {
        if (window.location.href !== href) return;
        const content = document.querySelector(".layout-content");
        if (content) content.scrollTop = scrollTop;
      },
      { once: true },
    );
    window.Turbo.visit(window.location.href, { action: "replace" });
  }
}

// The control that opened or closed the shelf disappears with it, so focus
// moves to what is shown next: the message box, or the rail's Regi button.
function focusAfterToggle(selector) {
  requestAnimationFrame(() => document.querySelector(`#chat-sidebar ${selector}`)?.focus());
}

function closeSidebar() {
  document.getElementById("chat-sidebar")?.classList.remove("chat-sidebar-open");
  localStorage.setItem("admin:chat-sidebar:open", "0");
  focusAfterToggle(".regi-rail-trigger");
}

function openSidebar() {
  document.getElementById("chat-sidebar")?.classList.add("chat-sidebar-open");
  localStorage.setItem("admin:chat-sidebar:open", "1");
  focusAfterToggle(".regi-chat textarea");
}

export const ChatPanel = () => {
  const orgId = useMemo(readOrgId, []);
  const [pageContext, setPageContext] = useState(readPageContext);
  const subscriptionRef = useRef(null);
  const listenerRef = useRef(null);

  useEffect(() => {
    const handler = () => setPageContext(readPageContext());
    document.addEventListener("turbo:load", handler);
    return () => document.removeEventListener("turbo:load", handler);
  }, []);

  // Turbo re-attaches the permanent sidebar on every render, which resets scrollTop.
  useEffect(() => {
    let scrolled = [];
    const save = () => {
      const sidebar = document.getElementById("chat-sidebar");
      scrolled = [...(sidebar?.querySelectorAll("*") ?? [])]
        .filter((el) => el.scrollTop > 0)
        .map((el) => [el, el.scrollTop]);
    };
    const restore = () => scrolled.forEach(([el, top]) => { el.scrollTop = top; });
    document.addEventListener("turbo:before-render", save);
    document.addEventListener("turbo:render", restore);
    return () => {
      document.removeEventListener("turbo:before-render", save);
      document.removeEventListener("turbo:render", restore);
    };
  }, []);

  useEffect(() => {
    if (!orgId) return;
    const sub = getSubscription(
      getConsumer(),
      "RegiAssistantChannel",
      { organization_id: orgId },
      {
        received: (data) => {
          if (!data) return;
          listenerRef.current?.(data);
          refreshAfterApply(data);
        },
      },
    );
    subscriptionRef.current = sub;
    return () => sub.unsubscribe();
  }, [orgId]);

  const subscribe = useCallback((onMessage) => {
    listenerRef.current = onMessage;
    return () => { listenerRef.current = null; };
  }, []);

  const ask = useCallback((payload) => {
    subscriptionRef.current?.perform("ask", payload);
  }, []);

  if (!orgId) return null;

  return (
    <RegiAssistantPanel
      subscribe={subscribe}
      ask={ask}
      pageContext={pageContext}
      subtitle={pageContext?.event_name}
      chatStorageKey={document.body.dataset.regiChatKey}
      onClose={closeSidebar}
      onExpand={openSidebar}
    />
  );
};
